import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { BUDGETING_QUERY_KEYS } from './keys';
import {
  fetchBudgetForEdit,
  fetchBudgetingCapabilities,
  fetchProjectCostCentres,
  fetchProjectZones,
} from './services';
import type {
  EditableBudget,
  IBudgetCapabilityResponse,
  IBudgetZoneListResponse,
  IProjectCostCentresResponse,
} from 'pages/BudgetWizard/types/response';

export const useBudgetingCapabilitiesQuery =
  (): UseQueryResult<IBudgetCapabilityResponse> =>
    useQuery({
      queryKey: [BUDGETING_QUERY_KEYS.CAPABILITIES],
      queryFn: () => fetchBudgetingCapabilities(),
      // Fail fast: a failed capability read is treated as "disabled" by the
      // consuming hook, so retrying only delays the fail-closed decision.
      retry: false,
      // The flag is stable within a session; avoid needless refetches.
      staleTime: 5 * 60 * 1000,
    });

export const useProjectZonesQuery = (
  projectId: string | undefined,
): UseQueryResult<IBudgetZoneListResponse> =>
  useQuery({
    queryKey: [BUDGETING_QUERY_KEYS.PROJECT_ZONES, projectId ?? ''],
    queryFn: () => fetchProjectZones(projectId as string),
    // Only fetch once a project id is known — the zone list is project-scoped.
    enabled: Boolean(projectId),
    retry: false,
  });

// Load a persisted budget in the flat, grid-editable shape. Only fetches once
// both the project id and the budget reference are known (the read is scoped to
// both). `options` lets a caller override defaults (e.g. keep it disabled while
// the grid is in create mode).
export const useBudgetForEditQuery = (
  projectId: string | undefined,
  reference: string | undefined,
  options?: Partial<UseQueryOptions<EditableBudget>>,
): UseQueryResult<EditableBudget> =>
  useQuery({
    queryKey: [
      BUDGETING_QUERY_KEYS.BUDGET_FOR_EDIT,
      projectId ?? '',
      reference ?? '',
    ],
    queryFn: () => fetchBudgetForEdit(projectId as string, reference as string),
    enabled: Boolean(projectId) && Boolean(reference),
    retry: false,
    ...options,
  });

// A project's cost centres in the `{ assigned, unassigned }` grouping. Returns
// data shaped for the shared `useCostCentreOptions` transformer — the consumer
// pipes `data` straight into it to get id-valued grid options. Only fetches
// once a project id is known.
export const useProjectCostCentresQuery = (
  projectId: string | undefined,
): UseQueryResult<IProjectCostCentresResponse> =>
  useQuery({
    queryKey: [BUDGETING_QUERY_KEYS.PROJECT_COST_CENTRES, projectId ?? ''],
    queryFn: () => fetchProjectCostCentres(projectId as string),
    enabled: Boolean(projectId),
    retry: false,
  });
